import { AppError } from "models/app-error";
import { vgApiRequest } from "./vg-api.service";
import { CONTACT_TYPE } from "./contacts.service";

type ListResult<T> = {
  data: T[];
  error: AppError | null;
};

export type ClientRow = {
  id: string;
  image: string;
  name: string;
  email: string;
};

export type StaffRow = {
  id: string;
  name: string;
  role: string;
};

export type ServiceRow = {
  id: string;
  name: string;
  description: string;
  configs: number;
};

export type ClinicRow = {
  id: string;
  name: string;
};

export const getClientsList = async (
  accessToken: string,
  orgId: number
): Promise<ListResult<ClientRow>> => {
  const { data, error } = await vgApiRequest<{ contactList?: any[]; ContactList?: any[] }>(
    accessToken,
    "Read_All_Organisation_Contacts",
    { orgId }
  );

  const list = data?.contactList ?? data?.ContactList ?? [];
  // Backend returns every contact for the org (UserMembers, Suppliers, Customers).
  // The clients page is customer-only, so drop anything that isn't a Customer
  // (or an unset contactType, which we treat as "show it" to avoid hiding legacy
  // rows). Matches ENUM_SYS_ContactType: UserMember=2 stays out, Customer=3 stays in.
  const filtered = list.filter((item: any) => {
    const t = Number(item.contactType ?? item.ContactType);
    if (!Number.isFinite(t) || t === 0) return true;
    return t === CONTACT_TYPE.CUSTOMER;
  });
  return {
    data: filtered.map((item: any) => ({
      id: String(item.contactId ?? item.ContactId ?? item.id ?? ""),
      image: item.cloudFileUrl || item.CloudFileUrl || "N/A",
      name:
        [item.contactFirstName, item.contactLastName].filter(Boolean).join(" ").trim() ||
        item.name ||
        "Unknown",
      email: item.emailAddress || item.EmailAddress || item.email || "N/A",
    })),
    error,
  };
};

/** POST `Read_All_Organisation_Users` — VG-WEB staff list; response `userList`. */
export const getStaffList = async (
  accessToken: string,
  orgId: number
): Promise<ListResult<StaffRow>> => {
  const { data, error } = await vgApiRequest<{ userList?: any[]; UserList?: any[] }>(
    accessToken,
    "Read_All_Organisation_Users",
    { orgId }
  );

  const list = data?.userList ?? data?.UserList ?? [];
  return {
    data: list.map((item: any) => ({
      id: String(item.userId ?? item.UserId ?? item.memberId ?? item.MemberId ?? ""),
      name:
        item.contactList?.[0]?.contactFullName ||
        [item.contactList?.[0]?.contactFirstName, item.contactList?.[0]?.contactLastName].filter(Boolean).join(" ").trim() ||
        "Unknown",
      role: item.roleName || item.RoleName || item.role || "Staff",
    })),
    error,
  };
};

/** POST `Read_All_Organisation_Services` — response `serviceCompleteList`. */
export const getServicesList = async (
  accessToken: string,
  orgId: number
): Promise<ListResult<ServiceRow>> => {
  const { data, error } = await vgApiRequest<{
    serviceCompleteList?: any[];
    ServiceCompleteList?: any[];
  }>(accessToken, "Read_All_Organisation_Services", { orgId });

  const list = data?.serviceCompleteList ?? data?.ServiceCompleteList ?? [];
  return {
    data: list.map((item: any) => {
      const configs = item.serviceFulfilmentConfigCompleteList ?? item.ServiceFulfilmentConfigCompleteList;
      const configCount = Array.isArray(configs) ? configs.length : Number(item.bookingConfigCount ?? 0);
      return {
        id: String(item.serviceId ?? item.ServiceId ?? ""),
        name: item.name ?? item.Name ?? "Unknown",
        description: item.description ?? item.Description ?? "N/A",
        configs: Number.isFinite(configCount) ? configCount : 0,
      };
    }),
    error,
  };
};

/** Clinics = organisations the signed-in user can access (`Read_All_User_Organisations`). */
export const getClinicsList = async (accessToken: string): Promise<ListResult<ClinicRow>> => {
  const { data, error } = await vgApiRequest<{
    completeOrgList?: any[];
    CompleteOrgList?: any[];
  }>(accessToken, "Read_All_User_Organisations", {});

  const list = data?.completeOrgList ?? data?.CompleteOrgList ?? [];
  return {
    data: list.map((item: any) => ({
      id: String(item.orgId ?? item.OrgId ?? ""),
      name: item.orgName ?? item.OrgName ?? "Unknown",
    })),
    error,
  };
};
